import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import React__default, { useMemo, useState, useEffect } from "react";
import { getAppConsistentParams } from "../utils/appUtils.js";
import { resolveProductImageUrls } from "../utils/assetUtils.js";
import { getProductUrl } from "../utils/productUtils.js";
import { decodeHTMLCharacters, formatMoney } from "../utils/stringUtils.js";
import { useBrandAppConfig } from "../api/cache/appConfigQueryOptions.js";
import { FallbackImage } from "../Components/FallbackImage.js";
import { customColor } from "@brandclub/bc-widgets";
import { ConciergeRetailerIconGroup } from "./components/ConciergeRetailerIconGroup.js";
const defaultCardStyles = {
  cardBorderColor: customColor("#dcdcdc"),
  cardBorderRadius: 18,
  cardBgColor: customColor("#ffffff"),
  cardBgColorHover: customColor("#f9fafb"),
  cardPaddingTop: 24,
  cardPaddingBottom: 16,
  cardPaddingSides: 12,
  titleColor: customColor("#000000"),
  titleFontFamily: "inherit",
  titleFontSize: 18,
  titleFontWeight: 500,
  titleLineClamp: 2,
  titleLineHeight: 1.33,
  titleMarginTop: 4,
  priceColor: customColor("#000000"),
  priceFontFamily: "inherit",
  priceFontSize: 24,
  priceFontSizeMobile: 20,
  priceFontWeight: 600,
  retailerIconSize: 25,
  retailerIconSizeMobile: 15,
  showRetailers: false,
  buttonBgColor: customColor("#000000"),
  buttonTextColor: customColor("#ffffff"),
  buttonBorderRadius: 100,
  buttonFontSize: 16,
  buttonFontWeight: 600,
  buttonHeight: 42,
  reviewColor: customColor("#000000"),
  reviewFontSize: 12,
  reviewFontWeight: 500,
  imageMaxWidth: 132
};
const defaultResolveColor = (color) => {
  if (typeof color === "string") return color;
  if (color && typeof color === "object" && "value" in color) return color.value;
  return "#000000";
};
const ConciergeProductCard = React__default.memo(
  ({
    product,
    cardStyles: externalCardStyles,
    themeFonts = {},
    resolveColor = defaultResolveColor,
    onProductClick
  }) => {
    const {
      retailPrice,
      stacklineSku,
      title,
      reviewsRating,
      reviewsAverageRating,
      reviewsCount,
      dtcProductPageUrl,
      retailerId,
      matchedProducts
    } = product;
    const retailerIds = useMemo(() => {
      const ids = [];
      if (retailerId) ids.push(retailerId);
      if (matchedProducts) {
        for (const mp of matchedProducts) {
          if (mp.retailerId && !ids.includes(mp.retailerId)) {
            ids.push(mp.retailerId);
          }
        }
      }
      return ids;
    }, [retailerId, matchedProducts]);
    const ratingValue = reviewsRating != null && reviewsRating > 0 ? Math.round(reviewsRating * 10) / 10 : reviewsAverageRating != null && reviewsAverageRating > 0 ? Math.round(reviewsAverageRating * 10) / 10 : null;
    const styles = externalCardStyles ? { ...defaultCardStyles, ...externalCardStyles } : defaultCardStyles;
    const productImage = resolveProductImageUrls(product);
    const cleanProductTitle = decodeHTMLCharacters(title || "");
    const urlSearchParams = getAppConsistentParams(window.location.search);
    const { data: brandAppConfig } = useBrandAppConfig();
    const targetUrl = getProductUrl({
      title,
      stacklineSku,
      urlSearchParams,
      dtcProductPageUrl,
      appConfigId: brandAppConfig == null ? void 0 : brandAppConfig.appConfigId
    });
    const [isMobileView, setIsMobileView] = useState(() => {
      if (typeof window === "undefined") return false;
      return window.innerWidth < 500;
    });
    useEffect(() => {
      const handleResize = () => {
        setIsMobileView(window.innerWidth < 500);
      };
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);
    const priceFontSize = isMobileView ? styles.priceFontSizeMobile : styles.priceFontSize;
    const titleFontKey = styles.titleFontFamily;
    const titleFontData = titleFontKey && titleFontKey !== "inherit" ? themeFonts[titleFontKey] : null;
    const titleFontFamily = titleFontData ? `"${titleFontData.name}", sans-serif` : "inherit";
    const priceFontKey = styles.priceFontFamily;
    const priceFontData = priceFontKey && priceFontKey !== "inherit" ? themeFonts[priceFontKey] : null;
    const priceFontFamily = priceFontData ? `"${priceFontData.name}", sans-serif` : "inherit";
    const fontFaces = [];
    if (titleFontData && titleFontData.format !== "google" && titleFontData.format !== "system") {
      fontFaces.push(`
      @font-face {
        font-family: "${titleFontData.name}";
        src: url("${titleFontData.url}") format("${titleFontData.format}");
        font-display: swap;
      }
    `);
    }
    if (priceFontData && priceFontData.name !== (titleFontData == null ? void 0 : titleFontData.name) && priceFontData.format !== "google" && priceFontData.format !== "system") {
      fontFaces.push(`
      @font-face {
        font-family: "${priceFontData.name}";
        src: url("${priceFontData.url}") format("${priceFontData.format}");
        font-display: swap;
      }
    `);
    }
    const fontFaceCss = fontFaces.join("\n");
    const cardStyle = {
      border: `1.5px solid ${resolveColor(styles.cardBorderColor)}`,
      width: "100%",
      borderRadius: styles.cardBorderRadius,
      paddingTop: styles.cardPaddingTop,
      paddingBottom: styles.cardPaddingBottom,
      paddingLeft: styles.cardPaddingSides,
      paddingRight: styles.cardPaddingSides,
      boxSizing: "border-box",
      transition: "background-color 0.4s ease-in-out",
      display: "flex",
      flexDirection: "column",
      alignItems: "center"
    };
    const titleStyle = {
      textDecoration: "none",
      color: resolveColor(styles.titleColor),
      ...titleFontFamily !== "inherit" && { fontFamily: titleFontFamily },
      fontSize: styles.titleFontSize,
      fontWeight: styles.titleFontWeight,
      marginTop: styles.titleMarginTop,
      width: "100%",
      overflow: "hidden",
      WebkitLineClamp: styles.titleLineClamp,
      lineHeight: styles.titleLineHeight,
      height: `${styles.titleLineClamp * styles.titleLineHeight}em`,
      display: "-webkit-box",
      WebkitBoxOrient: "vertical",
      wordBreak: "break-word"
    };
    const priceContainerStyle = {
      color: resolveColor(styles.priceColor),
      ...priceFontFamily !== "inherit" && { fontFamily: priceFontFamily },
      fontSize: priceFontSize,
      fontWeight: styles.priceFontWeight,
      lineHeight: 1.5
    };
    const imageContainerStyle = {
      width: "calc(100% - 24px)",
      maxWidth: styles.imageMaxWidth,
      aspectRatio: "1/1",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      margin: "0 auto 0"
    };
    const [isHovered, setIsHovered] = useState(false);
    return /* @__PURE__ */ jsxs(
      "div",
      {
        style: {
          ...cardStyle,
          backgroundColor: isHovered ? resolveColor(styles.cardBgColorHover) : resolveColor(styles.cardBgColor)
        },
        onMouseEnter: () => setIsHovered(true),
        onMouseLeave: () => setIsHovered(false),
        children: [
          fontFaceCss && /* @__PURE__ */ jsx("style", { children: fontFaceCss }),
          /* @__PURE__ */ jsxs(
            "div",
            {
              className: "concierge-product-card",
              style: { width: "100%", display: "flex", flexDirection: "column", alignItems: "center", flex: 1 },
              children: [
                /* @__PURE__ */ jsx("a", { href: targetUrl, target: "_blank", rel: "noreferrer", style: imageContainerStyle, onClick: onProductClick, children: /* @__PURE__ */ jsx(
                  FallbackImage,
                  {
                    ...productImage,
                    alt: title,
                    style: {
                      maxHeight: "100%",
                      maxWidth: "100%",
                      aspectRatio: "1",
                      objectFit: "contain",
                      mixBlendMode: "multiply"
                    }
                  }
                ) }),
                /* @__PURE__ */ jsxs("div", { style: { width: "100%", display: "flex", flexDirection: "column", gap: 6, marginTop: 16 }, children: [
                  /* @__PURE__ */ jsx(
                    "div",
                    {
                      style: {
                        display: "flex",
                        alignItems: "center",
                        gap: 3,
                        fontSize: styles.reviewFontSize,
                        fontWeight: styles.reviewFontWeight,
                        lineHeight: "20px",
                        minHeight: 20,
                        color: resolveColor(styles.reviewColor)
                      },
                      children: ratingValue != null && /* @__PURE__ */ jsxs(Fragment, { children: [
                        /* @__PURE__ */ jsx("span", { style: { color: "#ED792F", fontSize: styles.reviewFontSize, lineHeight: 1 }, children: "★" }),
                        /* @__PURE__ */ jsxs("span", { children: [
                          ratingValue,
                          reviewsCount != null && reviewsCount > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
                            " (",
                            reviewsCount >= 1e3 ? `${(reviewsCount / 1e3).toFixed(1)}K` : reviewsCount,
                            " ratings)"
                          ] })
                        ] })
                      ] })
                    }
                  ),
                  /* @__PURE__ */ jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 4, width: "100%" }, children: [
                    /* @__PURE__ */ jsx("a", { href: targetUrl, target: "_blank", rel: "noreferrer", style: titleStyle, onClick: onProductClick, children: cleanProductTitle }),
                    /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: 6 }, children: [
                      /* @__PURE__ */ jsx("div", { style: priceContainerStyle, children: formatMoney(retailPrice) }),
                      styles.showRetailers && retailerIds.length > 0 && /* @__PURE__ */ jsx(
                        ConciergeRetailerIconGroup,
                        {
                          retailerIds,
                          iconSize: isMobileView ? styles.retailerIconSizeMobile : styles.retailerIconSize
                        }
                      )
                    ] })
                  ] })
                ] }),
                /* @__PURE__ */ jsx("div", { style: { flex: 1, minHeight: 8 } }),
                /* @__PURE__ */ jsx(
                  "a",
                  {
                    href: targetUrl,
                    target: "_blank",
                    rel: "noreferrer",
                    onClick: onProductClick,
                    style: {
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      height: styles.buttonHeight,
                      backgroundColor: resolveColor(styles.buttonBgColor),
                      color: resolveColor(styles.buttonTextColor),
                      borderRadius: styles.buttonBorderRadius,
                      fontSize: styles.buttonFontSize,
                      fontWeight: styles.buttonFontWeight,
                      lineHeight: "27px",
                      textDecoration: "none",
                      cursor: "pointer",
                      transition: "opacity 0.2s ease",
                      flexShrink: 0
                    },
                    children: "View product"
                  }
                )
              ]
            }
          )
        ]
      }
    );
  },
  (prevProps, nextProps) => {
    return prevProps.product.stacklineSku === nextProps.product.stacklineSku && prevProps.product.retailPrice === nextProps.product.retailPrice && prevProps.product.title === nextProps.product.title && prevProps.product.affiliateRewardAmount === nextProps.product.affiliateRewardAmount && prevProps.product.hasActiveInstantReward === nextProps.product.hasActiveInstantReward && prevProps.showCartActions === nextProps.showCartActions && JSON.stringify(prevProps.cardStyles) === JSON.stringify(nextProps.cardStyles);
  }
);
ConciergeProductCard.displayName = "ConciergeProductCard";
export {
  ConciergeProductCard
};
//# sourceMappingURL=ConciergeProductCard.js.map
